import { z } from 'zod';

/**
 * Progression du joueur (concept §4.3) : étoiles gagnées et déblocages,
 * persistée côté client en `localStorage` (V1, par jeu/world).
 *
 * Le calcul des étoiles est fait par le moteur au moment de la victoire — ce
 * schéma ne fait que valider et conserver le MEILLEUR résultat obtenu.
 * Comme toute donnée lue depuis `localStorage`, elle est non fiable (JSON
 * corrompu, ancien format…) : la frontière est donc validée par zod.
 */

/** Meilleur résultat obtenu sur un niveau. */
export const LevelProgressSchema = z.object({
  stars: z.number().int().min(0).max(3),
  bestTimeMs: z.number().nonnegative().nullable(),
  bestBolts: z.number().int().nonnegative(),
  /** Morts du meilleur run (celui qui a donné le plus d'étoiles). */
  deaths: z.number().int().nonnegative(),
  /** Horodatage (ms) du premier succès sur ce niveau. */
  completedAt: z.number().int().nonnegative(),
});

/** Progression complète, indexée par identifiant de niveau. */
export const ProgressStateSchema = z.object({
  version: z.literal(1),
  levels: z.record(z.string(), LevelProgressSchema),
});

export type LevelProgress = z.infer<typeof LevelProgressSchema>;
export type ProgressState = z.infer<typeof ProgressStateSchema>;

/** Progression initiale : aucun niveau terminé. */
export const EMPTY_PROGRESS: ProgressState = {
  version: 1,
  levels: {},
};

/**
 * `b` est-il le meilleur run des deux ? Plus d'étoiles d'abord, meilleur
 * temps ensuite — le même ordre que celui du store de progression local.
 */
function isBetterRun(a: LevelProgress, b: LevelProgress): boolean {
  if (b.stars !== a.stars) {
    return b.stars > a.stars;
  }
  return a.bestTimeMs === null || (b.bestTimeMs !== null && b.bestTimeMs < a.bestTimeMs);
}

/**
 * Fusionne deux résultats d'un même niveau en ne gardant que le meilleur de
 * chaque grandeur. Commutatif et idempotent : fusionner deux fois, ou dans
 * l'autre sens, donne le même résultat — c'est ce qui rend la synchronisation
 * compte/appareils sûre quel que soit l'ordre des échanges.
 */
export function mergeLevelProgress(a: LevelProgress, b: LevelProgress): LevelProgress {
  const best = isBetterRun(a, b) ? b : a;
  return {
    stars: Math.max(a.stars, b.stars),
    bestTimeMs:
      a.bestTimeMs === null
        ? b.bestTimeMs
        : b.bestTimeMs === null
          ? a.bestTimeMs
          : Math.min(a.bestTimeMs, b.bestTimeMs),
    bestBolts: Math.max(a.bestBolts, b.bestBolts),
    deaths: best.deaths,
    completedAt: Math.min(a.completedAt, b.completedAt),
  };
}

/** Fusionne deux progressions complètes, niveau par niveau. */
export function mergeProgressStates(a: ProgressState, b: ProgressState): ProgressState {
  const levels: Record<string, LevelProgress> = { ...a.levels };
  for (const [levelId, entry] of Object.entries(b.levels)) {
    const existing = levels[levelId];
    levels[levelId] = existing ? mergeLevelProgress(existing, entry) : entry;
  }
  return { version: 1, levels };
}
